
import { Event } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { WaitlistDialog } from "./WaitlistDialog";
import { getCurrencySymbol } from "@/lib/currencyUtils";

interface EventTicketCTAProps {
  event: Event;
}

export const EventTicketCTA = ({
  event
}: EventTicketCTAProps) => {
  const navigate = useNavigate();
  const currencySymbol = getCurrencySymbol(event.currency);

  // Check if event is more than 5 hours past
  const eventDate = new Date(event.date);
  const currentTime = new Date();
  const fiveHoursInMs = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  const isEventPast = currentTime.getTime() - eventDate.getTime() > fiveHoursInMs;

  // Check if event is sold out
  const isSoldOut = () => {
    const hasEarlyBird = event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0;
    const hasGeneralAdmission = event.price_general_admission && event.quantity_standing && event.quantity_standing > 0;
    const hasVip = event.price_vip && event.quantity_vip && event.quantity_vip > 0;
    
    return !hasEarlyBird && !hasGeneralAdmission && !hasVip;
  };

  // Calculate the cheapest ticket price including Slay fee
  const calculateTotalPrice = () => {
    const prices = [];

    // Add available ticket prices
    if (event.early_bird_price && event.quantity_early_bird && event.quantity_early_bird > 0) {
      prices.push(event.early_bird_price);
    }
    if (event.price_general_admission && event.quantity_standing && event.quantity_standing > 0) {
      prices.push(event.price_general_admission);
    }
    if (event.price_vip && event.quantity_vip && event.quantity_vip > 0) {
      prices.push(event.price_vip);
    }

    // Find the cheapest price
    const cheapestPrice = Math.min(...prices);

    // Calculate Slay fee
    const slayFeePercentage = event.slay_fee_percentage || 0;
    const slayFeeFixed = event.slay_fee_fixed || 0;
    const slayFeeAmount = cheapestPrice * slayFeePercentage / 100 + slayFeeFixed;

    // Return total price including Slay fee
    return cheapestPrice + slayFeeAmount;
  };

  const totalPrice = calculateTotalPrice();
  const soldOut = isSoldOut();

  const handleGetTicketsClick = () => {
    navigate(`/events/${event.seo_url}/ticketing`);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const renderButton = () => {
    // Don't show waitlist for events that have passed more than 5 hours ago
    if (soldOut && !isEventPast) {
      return (
        <WaitlistDialog eventTitle={event.title}>
          <Button 
            className="w-full relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:shadow-xl hover:scale-[1.02] bg-[length:200%_100%] text-xl py-6 text-white font-bold tracking-wide rounded-lg shadow-lg transition-all duration-300 ease-out active:scale-[0.98] active:shadow-inner before:absolute before:inset-0 before:bg-white/10 before:rounded-lg before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 min-h-[48px] group-hover:bg-pos-100 backdrop-filter backdrop-blur-sm"
            aria-label="Join waitlist for this event"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <span className="text-2xl">🎟️</span>
              Join Waitlist
            </span>
          </Button>
        </WaitlistDialog>
      );
    }

    return (
      <Button 
        onClick={handleGetTicketsClick}
        disabled={isEventPast} 
        className={`
          w-full relative overflow-hidden
          ${isEventPast ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400' : 'bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:shadow-xl hover:scale-[1.02]'}
          bg-[length:200%_100%]
          text-xl py-6 text-white font-bold tracking-wide
          rounded-lg shadow-lg
          transition-all duration-300 ease-out
          active:scale-[0.98] active:shadow-inner
          disabled:opacity-70 disabled:cursor-not-allowed
          before:absolute before:inset-0
          before:bg-white/10 before:rounded-lg
          before:translate-x-[-100%] hover:before:translate-x-[100%]
          before:transition-transform before:duration-500
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
          min-h-[48px] aria-label='Purchase tickets for this event'
          group-hover:bg-pos-100 backdrop-filter backdrop-blur-sm
        `} 
        aria-label="Purchase tickets for this event"
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          <span className="text-2xl">🎟️</span>
          {isEventPast ? "You Missed It 👀" : "Get Tickets"}
          {!isEventPast && (
            <span className="ml-2 text-sm font-normal opacity-80">
              From <span className="hidden sm:inline">(inc. fees) </span>{currencySymbol}{totalPrice.toFixed(2)}
            </span>
          )}
        </span>
      </Button>
    );
  };

  return (
    <div className="bg-white border-b border-gray-200 p-6">
      <div className="relative group">
        {/* Only show sold out message for active events, not past events */}
        {soldOut && !isEventPast ? (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-4 text-center">
            <p className="text-red-400 font-bold text-xl flex items-center justify-center gap-2">
              <span className="text-2xl">🔥</span>
              All Tickets Snatched. Join the Waitlist.
            </p>
          </div>
        ) : (
          event.quantity_standing < 20 && !isEventPast && (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-2 mb-4 text-center">
              <p className="text-yellow-600 font-bold text-sm">
                🔥 Selling Fast! Only {event.quantity_standing} tickets left
              </p>
            </div>
          )
        )}
        
        {renderButton()}
      </div>
    </div>
  );
};
